import { Link, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useAuth } from "../contexts/AuthContext";
import { hideToast } from "../store/slices/notificationsSlice";
import onboardingService from "../services/onboardingService";
import { useKeyboardNavigation, useScreenReader } from "../hooks/useAccessibility";
import { useNavRoutes } from "../hooks/useNavRoutes";
import ThemeToggle from "./common/ThemeToggle";
import NavGroup from "./NavGroup";
import NavItem from "./NavItem";
import MobileMenu from "./MobileMenu";

function Navigation() {
  const { user, logout } = useAuth();
  const dispatch = useDispatch();
  const location = useLocation();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [onboardingCompleted, setOnboardingCompleted] = useState(false);
  const [openGroupId, setOpenGroupId] = useState(null);

  const notificationRef = useRef(null);
  const userMenuRef = useRef(null);

  const toasts = useSelector((state) => state.notifications.toasts);
  const { announce } = useScreenReader();
  const { groups, dashboardLink, unauthLinks } = useNavRoutes(user, onboardingCompleted);

  const { onKeyDown: handleNotificationKeyDown } = useKeyboardNavigation({
    onEscape: () => setIsNotificationOpen(false),
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setIsNotificationOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };
    if (isNotificationOpen || isUserMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isNotificationOpen, isUserMenuOpen]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!user) return;
    onboardingService.checkOnboardingStatus().then((status) => {
      setOnboardingCompleted(status.isCompleted);
    }).catch(() => {
      setOnboardingCompleted(false);
    });
  }, [user]);

  const toggleMobileMenu = () => {
    const next = !isMobileMenuOpen;
    setIsMobileMenuOpen(next);
    announce(next ? "Mobile menu opened" : "Mobile menu closed");
  };

  const toggleNotifications = () => {
    const next = !isNotificationOpen;
    setIsNotificationOpen(next);
    setIsUserMenuOpen(false);
    announce(next ? "Notifications opened" : "Notifications closed");
  };

  const toggleUserMenu = () => {
    const next = !isUserMenuOpen;
    setIsUserMenuOpen(next);
    setIsNotificationOpen(false);
    announce(next ? "User menu opened" : "User menu closed");
  };

  return (
    <nav
      className="bg-card-bg/95 backdrop-blur-md shadow-sm border-b border-card-border sticky top-0 z-50"
      role="navigation"
      aria-label="Main navigation"
      id="navigation"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link
            to="/"
            className="flex items-center space-x-3 group focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 rounded-lg p-1 -m-1"
            aria-label="GrowthTracker home"
          >
            <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform duration-200">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
            <span className="hidden sm:block text-xl font-bold text-text-primary">GrowthTracker</span>
          </Link>

          {user && (
            <div className="hidden lg:flex items-center gap-1">
              {dashboardLink && (
                <NavItem path={dashboardLink.path} label={dashboardLink.label} />
              )}
              {groups.map((group) => (
                <NavGroup
                  key={group.id}
                  group={group}
                  isOpen={openGroupId === group.id}
                  onOpen={setOpenGroupId}
                  onClose={() => setOpenGroupId(null)}
                />
              ))}
            </div>
          )}

          <div className="flex items-center gap-2">
            <ThemeToggle />

            {user && (
              <div className="relative" ref={notificationRef} onKeyDown={handleNotificationKeyDown}>
                <button
                  onClick={toggleNotifications}
                  aria-label="Notifications"
                  aria-expanded={isNotificationOpen}
                  className="relative p-2 text-text-secondary hover:text-text-primary hover:bg-bg-secondary rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  {toasts.length > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                      {toasts.length > 9 ? "9+" : toasts.length}
                    </span>
                  )}
                </button>

                {isNotificationOpen && (
                  <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-card-bg/95 backdrop-blur-md rounded-xl shadow-xl border border-card-border z-50 max-h-96 overflow-hidden">
                    <div className="p-4 border-b border-card-border flex items-center justify-between">
                      <h3 className="text-base font-semibold text-text-primary">Notifications</h3>
                      <button
                        onClick={() => setIsNotificationOpen(false)}
                        aria-label="Close notifications"
                        className="p-1 text-text-tertiary hover:text-text-secondary rounded-lg hover:bg-bg-secondary transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      {toasts.length > 0 ? (
                        <div className="p-2 space-y-1">
                          {toasts.map((toast) => (
                            <div key={toast.id} className="flex items-start gap-3 p-3 rounded-xl hover:bg-bg-secondary transition-colors">
                              <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm font-medium ${
                                toast.type === "success" ? "bg-emerald-100 text-emerald-600"
                                : toast.type === "error" ? "bg-red-100 text-red-600"
                                : toast.type === "warning" ? "bg-amber-100 text-amber-600"
                                : "bg-blue-100 text-blue-600"
                              }`}>
                                {toast.type === "success" && "✓"}
                                {toast.type === "error" && "✕"}
                                {toast.type === "warning" && "!"}
                                {toast.type === "info" && "i"}
                              </div>
                              <p className="flex-1 text-sm text-text-primary">{toast.message}</p>
                              <button
                                onClick={() => dispatch(hideToast(toast.id))}
                                aria-label="Dismiss notification"
                                className="flex-shrink-0 p-1 text-text-tertiary hover:text-text-secondary rounded-lg hover:bg-bg-secondary transition-colors"
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="p-12 text-center">
                          <p className="text-sm text-text-secondary font-medium">No notifications</p>
                          <p className="text-xs text-text-tertiary mt-1">You&apos;re all caught up!</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {user ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={toggleUserMenu}
                  aria-expanded={isUserMenuOpen}
                  aria-haspopup="true"
                  aria-label="User menu"
                  className="flex items-center gap-2 p-2 rounded-xl hover:bg-bg-secondary transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
                >
                  <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center ring-2 ring-card-border">
                    <span className="text-white text-sm font-semibold">
                      {(user.first_name || user.username || "U").charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="hidden lg:block text-left">
                    <p className="text-sm font-medium text-text-primary">{user.first_name || user.username}</p>
                    <p className="text-xs text-text-secondary">{user.email}</p>
                  </div>
                  <svg className={`hidden lg:block w-4 h-4 text-text-tertiary transition-transform duration-150 ${isUserMenuOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {isUserMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-56 bg-card-bg rounded-xl shadow-lg border border-card-border py-2 z-50">
                    <div className="px-4 py-3 border-b border-card-border">
                      <p className="text-sm font-medium text-text-primary truncate">{user.first_name || user.username}</p>
                      <p className="text-xs text-text-secondary truncate">{user.email}</p>
                    </div>
                    <div className="py-1">
                      <Link to="/profile" onClick={() => setIsUserMenuOpen(false)} className="flex items-center gap-3 px-4 py-2 text-sm text-text-primary hover:bg-bg-secondary transition-colors">
                        Profile Settings
                      </Link>
                      <Link to="/dashboard" onClick={() => setIsUserMenuOpen(false)} className="flex items-center gap-3 px-4 py-2 text-sm text-text-primary hover:bg-bg-secondary transition-colors">
                        Dashboard
                      </Link>
                    </div>
                    <div className="border-t border-card-border pt-1">
                      <button
                        onClick={() => { logout(); setIsUserMenuOpen(false); }}
                        className="flex items-center gap-3 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                      >
                        Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="hidden sm:flex items-center gap-2">
                {unauthLinks.map((link) =>
                  link.variant === "primary" ? (
                    <Link
                      key={link.path}
                      to={link.path}
                      className="px-5 py-2 text-sm font-medium text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 rounded-xl shadow transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
                    >
                      {link.label}
                    </Link>
                  ) : (
                    <Link
                      key={link.path}
                      to={link.path}
                      className="px-4 py-2 text-sm font-medium text-text-primary hover:bg-bg-secondary rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
                    >
                      {link.label}
                    </Link>
                  )
                )}
              </div>
            )}

            <button
              onClick={toggleMobileMenu}
              className="lg:hidden p-2 text-text-secondary hover:text-text-primary hover:bg-bg-secondary rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
              aria-expanded={isMobileMenuOpen}
              aria-label="Toggle mobile menu"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>

      <MobileMenu
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        groups={groups}
        dashboardLink={dashboardLink}
        user={user}
      />
    </nav>
  );
}

export default Navigation;
